import React, { useCallback, useEffect, useState } from "react"
import { AFSClassification, CostTrialBalance, CTBRecords, CTBRequest, PPTClassification, PPTRamsesDescription, Reconciliation, TaxTerm } from "../../helpers/interfaces"
import { getAllAFSClassifications, getAllPPTClassifications, getAllPPTRameses, getAllReconciliation, getAllTaxTerm } from "../../apiServices/CTBMappingKeysApi"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Menu, MenuItem, Pagination, Stack, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableHead, TableRow, useTheme } from "@mui/material"
import { exportAllMappedCTB, exportExportAllUnmappedCTB, exportExportUnmappedCTB, exportMappedCTB, getCTBRecords, getJob, UpdateCTBRecords, uploadCBTRecordsExcelFile } from "../../apiServices/CTBRecordsApi"
import saveAs from "file-saver"
import { CustomTextField } from "../../layouts/themes"
import { Download, Save, Upload } from "@mui/icons-material"
import { isMapped } from "../../helpers/constants"
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state"
import styled from "@emotion/styled"
import { formatShortDateString, showAlertMessage } from "../../helpers/utils"
import Preloader from "../preloader/Preloader"



interface Props {
    year: number
    setYear: React.Dispatch<React.SetStateAction<number>>
    mappingStatus: isMapped
    setSelectedTabValue: React.Dispatch<React.SetStateAction<number>>
    selectedTabValue: number
}
function CTBalanceRecords({ year, setYear, mappingStatus, setSelectedTabValue, selectedTabValue }: Props) {
    const theme = useTheme();

    const [costTrialBlanceRecords, setCostTrialBlanceRecords] = useState<CTBRecords[]>([])

    const [searchQuery, setSearchQuery] = useState<string>("");

    const [recordsPerPage, setRecordsPerPage] = useState<number>(20);
    const [numOfPages, setNumOfPages] = useState<number>(0);
    const [currentPageRecords, setCurrentPageRecords] = useState<CTBRecords[]>([])
    const [ctbDataRequest, setCTBDataRequest] = useState<CTBRecords[]>([])
    const [afsClassification, setAfsClassification] = React.useState<AFSClassification[]>([])
    const [pptClassification, setPPTClassification] = React.useState<PPTClassification[]>([])
    const [reconciliation, setReconciliation] = React.useState<Reconciliation[]>([])
    const [pptRamses, setPPTRamses] = React.useState<PPTRamsesDescription[]>([])
    const [taxTerm, setTaxTerm] = React.useState<TaxTerm[]>([])
    const [currentPage, setCurrentPage] = useState(1);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [jobID, setJobID] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<string | null>(null);
    const [changeYear, setChangeYear] = useState(false)
    const [changedYear, setChangedYear] = useState(0)
    const [loading, setLoading] = useState(false)
    const [selectedMonth, setSelectedMonth] = useState<string>("All");
    const getCTB = useCallback(async () => {
        setLoading(true)
        let data: CostTrialBalance = await getCTBRecords(mappingStatus, recordsPerPage, currentPage, year)

        setCostTrialBlanceRecords(data.data)
        setCurrentPage(data.pageIndex)
        setNumOfPages(data.totalPages)
        setRecordsPerPage(data.pageSize)
        setCurrentPageRecords(data.data)

    }, [currentPage, mappingStatus, recordsPerPage, year]);

    useEffect(() => {
        getCTB();
        setLoading(false)
    }, [getCTB]);

    useEffect(() => {
        getAllAFSClassifications().then(afs => setAfsClassification(afs))
        getAllPPTClassifications().then(ppt => setPPTClassification(ppt))
        getAllReconciliation().then(rec => setReconciliation(rec))
        getAllPPTRameses().then(ramses => setPPTRamses(ramses))
        getAllTaxTerm().then(tax => setTaxTerm(tax))
    }, [])


    // function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    //     e.persist(); //important to persist event so we can reset the value of the input file after an upload
    //     if (e.target.files == null || e.target.files.length === 0) return
    //     const formData = new FormData()
    //     formData.append('MappedFile', e.target.files[0])
    //     uploadCBTRecordsExcelFile(formData, year).then(res => {
    //         setJobID(res.data.id);
    //         setJobStatus(res.data.status);
    //         console.log(jobStatus)
    //         showAlertMessage(res.message);
    //     })
    //     e.target.value = '' //important to persist event so we can reset the value of the input file after an upload
    // }

    useEffect(() => {
        if (!jobID) return;

        const interval = setInterval(async () => {
            try {
                const jobData = await getJob(jobID);
                setJobStatus(jobData.status);

                if (jobData.status === "COMPLETED") {
                    clearInterval(interval);
                    showAlertMessage(jobData.message);
                    getCTBRecords(mappingStatus, recordsPerPage, currentPage, year).then(data => {
                        setCostTrialBlanceRecords(data.data)
                        setCurrentPage(data.pageIndex)
                        setNumOfPages(data.totalPages)
                        setRecordsPerPage(data.pageSize)
                        setCurrentPageRecords(data.data)
                    })

                }

                if (jobData.status === "FAILED") {
                    clearInterval(interval);
                    showAlertMessage("❌ Job failed. Please try again.");
                }
            } catch (err) {
                clearInterval(interval);
                showAlertMessage("⚠️ Error checking job status.");
            }
        }, 60000); // poll every 1 minute

        return () => clearInterval(interval);
    }, [currentPage, jobID, mappingStatus, recordsPerPage, year]);

  const updateFieldValue = (id: string, fieldName: string, newValue: any) => {

  const updatedRecords = costTrialBlanceRecords.map(rec =>

    rec.id === id ? { ...rec, [fieldName]: newValue } : rec

  );
 
  setCostTrialBlanceRecords(updatedRecords);
 
  // ✅ Also update currentPageRecords so UI reflects changes

  const updatedPageRecords = currentPageRecords.map(rec =>

    rec.id === id ? { ...rec, [fieldName]: newValue } : rec

  );

  setCurrentPageRecords(updatedPageRecords);
 
  // ✅ Track changes for mapping

  const record = updatedRecords.find(r => r.id === id)!;

  setCTBDataRequest(prev => {

    const existing = prev.find(e => e.id === id);

    if (existing) {

      return prev.map(e => (e.id === id ? record : e));

    }

    return [...prev, record];

  });

};

 

    function mapCTBRecords() {
        const cbtRequest: CTBRequest[] = ctbDataRequest.map(item => ({
            costTrialBalanceId: item.id,
            pptClassificationId: item.pptClassificationId,
            afsClassificationId: item.afsClassificationId,
            reconciliationId: item.reconciliationId,
            pptRamsesDescId: item.pptRamsesDescId,
            taxTermId: item.taxTermId,
            year: String(year)
        }));
        UpdateCTBRecords(cbtRequest)
        setSelectedTabValue(3)
    }
    function exportUnmappedCTBData() {
        exportExportUnmappedCTB(recordsPerPage, year).then(async (base64string) => {
            const blob = base64ToBlob(base64string, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            saveAs(blob, `CTB_PPT_${year}_Unmapped.xlsx`);
        })
    }
    function exportAllUnMappedCTBData() {
        exportExportAllUnmappedCTB(year).then(async (base64string) => {
            const blob = base64ToBlob(base64string, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            saveAs(blob, `CTB_PPT_${year}_Unmapped.xlsx`);
        })
    }
    function exportAllMappedCTBData() {
        exportAllMappedCTB(year).then(async (base64string) => {
            const blob = base64ToBlob(base64string, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            saveAs(blob, `CTB_PPT_${year}_Mapped.xlsx`);
        })
    }
    function exportMappedCTBData() {
        exportMappedCTB(recordsPerPage, year).then(async (base64string) => {
            const blob = base64ToBlob(base64string, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
            saveAs(blob, `CTB_PPT_${year}_Mapped.xlsx`);
        })
    }

    function base64ToBlob(base64string: string, type: string) {
        const binaryString = window.atob(base64string);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: type });
    }
    const handleClosePopupState = (popupState: { close: () => void }) => {
        popupState.close();
    };
    const StyledTableRow = styled(TableRow)({
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
            size: 'small',
        },
        border: '1px solid #cbcbcb !important',


    });
    const StyledTableCell = styled(TableCell)({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.action.hover,
            color: theme.palette.error.main,
            fontSize: 11,
            size: 'small',
            fontWeight: 550,
            border: '1px solid #cbcbcb !important',



        },
        [`&.${tableCellClasses.body}`]: {
            fontSize: 11,
            size: 'small',
            padding: 7,
            border: '1px solid #cbcbcb !important',



        },
    });
   const filteredRecords = currentPageRecords.filter((record) => {

  const costObjectMatch =

    record.costObject?.toLowerCase().includes(searchQuery.toLowerCase()) ||

    record.ctbObject?.toLowerCase().includes(searchQuery.toLowerCase()) ||

    record.jvName?.toLowerCase().includes(searchQuery.toLowerCase());
 
  if (selectedMonth === "All") return costObjectMatch;
 
  const recordMonth = new Date(record.createdAt).toLocaleString("default", {

    month: "long",

  });
 
  return costObjectMatch && recordMonth === selectedMonth;

});

 

    return (
        <>

            <div className=" flex flex-row mb-2 ">

                <div className="flex gap-2 w-1/2">
                    {/* Search */}
                    <CustomTextField
                        size="small"
                        placeholder="Search by Cost Object..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        variant="outlined"
                        sx={{ flex: 1 }}
                    />

                    {/* Month Filter */}
                    <CustomTextField
                        select
                        size="small"
                        label="Filter By Month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        sx={{ width: 180 }}
                    >
                        <MenuItem value="All">All Months</MenuItem>
                        {[
                            "January",
                            "February",
                            "March",
                            "April",
                            "May",
                            "June",
                            "July",
                            "August",
                            "September",
                            "October",
                            "November",
                            "December",
                        ].map((month) => (
                            <MenuItem key={month} value={month}>
                                {month}
                            </MenuItem>
                        ))}
                    </CustomTextField>
                </div>

                <div style={{ flexGrow: 1 }} />
                {selectedTabValue === 3 &&
                    <>

                        <Button
                            color="success"
                            variant="outlined"
                            onClick={() => setImportDialogOpen(true)}
                        >
                            <Upload /> &nbsp;&nbsp;Import CTB
                        </Button>
                        &nbsp;&nbsp;

                        <PopupState variant="popover" popupId="demo-popup-menu">
                            {(popupState) => (
                                <React.Fragment>
                                    <Button color='success' variant="outlined" size="small" {...bindTrigger(popupState)} disabled={costTrialBlanceRecords.length === 0}><img style={{ width: '20px' }} src='../../assets/images/icons8-excel.svg' alt="export Mapped CTB Data" />&nbsp;<b>Export</b> </Button>
                                    <Menu {...bindMenu(popupState)}>
                                        <MenuItem onClick={() => { exportAllMappedCTBData(); handleClosePopupState(popupState); }}> <Download color='secondary' /> &nbsp;&nbsp; Export All CTB Records</MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => { exportMappedCTBData(); handleClosePopupState(popupState) }}> <Download color='secondary' /> &nbsp;&nbsp; Export Current Page CTB Records</MenuItem>

                                    </Menu>
                                </React.Fragment>
                            )}
                        </PopupState>&nbsp;&nbsp;

                    </>
                }
                {selectedTabValue === 2 &&
                    <>

                        <PopupState variant="popover" popupId="demo-popup-menu">
                            {(popupState) => (
                                <React.Fragment>
                                    <Button color='success' variant="outlined" size="small" {...bindTrigger(popupState)} disabled={costTrialBlanceRecords.length === 0}><img style={{ width: '20px' }} src='../../assets/images/icons8-excel.svg' alt="export Mapped CTB Data" />&nbsp;<b>Export</b> </Button>
                                    <Menu {...bindMenu(popupState)}>
                                        <MenuItem onClick={() => { exportAllUnMappedCTBData(); handleClosePopupState(popupState); }}> <Download color='secondary' /> &nbsp;&nbsp; Export All CTB Records</MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => { exportUnmappedCTBData(); handleClosePopupState(popupState) }}> <Download color='secondary' /> &nbsp;&nbsp; Export  CTB Records</MenuItem>

                                    </Menu>
                                </React.Fragment>
                            )}
                        </PopupState>&nbsp;&nbsp;
                    </>
                }
                <Button color='info' size="small" variant="contained" onClick={mapCTBRecords} disabled={costTrialBlanceRecords.length === 0}> <Save sx={{ width: '20px' }} /> &nbsp;Save </Button>

            </div>

            <>
                <div className="">
                    <TableContainer className="table table-bordered table-sm table-responsive" sx={{ maxHeight: '56vh', overflowX: 'scroll', overflowY: 'auto', border: '1px solid rgba(224, 224, 224, 1)', '&::-webkit-scrollbar': { width: '6px', height: '6px', }, '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.1)', }, '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.3)', borderRadius: '4px', }, '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.5)', } }}    >
                        <Table size="small" aria-label="a dense table" aria-labelledby="tableTitle">
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell style={{ position: "sticky", left: 0, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}> SN</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '100px', position: "sticky", left: 100, zIndex: 1, background: `${theme.palette.error.contrastText}`, }}> JV Name</StyledTableCell>
                                    <StyledTableCell style={{ position: "sticky", left: 200, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}>Object Type</StyledTableCell>
                                    <StyledTableCell style={{ position: "sticky", left: 300, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}>Cost Object</StyledTableCell>
                                    <StyledTableCell style={{ position: "sticky", left: 400, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}>WBS DESC</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>Cost Element</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>GL DESC</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>Class 6 (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>Class 7 (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>Class 8 (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>All (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>Cutback 6 (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>WIP-Asset Tfer (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '150px' }}>CTB (TEK$)</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '300px' }}>PPT Classification</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '300px' }}>AFS Classification</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '300px' }}>Reconciliation</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '300px' }}>PPT Ramses Desc</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '300px' }}>Tax Term</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '100px' }}>Fetched At</StyledTableCell>
                                    <StyledTableCell style={{ fontSize: '12px', minWidth: '100px' }}>Last Updated</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading && <Preloader loading={loading} />}
                                {filteredRecords?.map((item, index) => {
                                    return <StyledTableRow key={index}>
                                        <StyledTableCell style={{ position: "sticky", left: 0, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }} >
                                            {index + 1 + ((currentPage - 1) * recordsPerPage)}
                                        </StyledTableCell>
                                        <StyledTableCell style={{ position: "sticky", left: 100, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}  >{item.jvName}</StyledTableCell>
                                        <StyledTableCell style={{ position: "sticky", left: 200, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }}  >{item.ctbObject}</StyledTableCell>
                                        <StyledTableCell style={{ position: "sticky", left: 300, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }} >{item.costObject}</StyledTableCell>
                                        <StyledTableCell style={{ position: "sticky", left: 400, zIndex: 1, background: `${theme.palette.error.contrastText}`, fontSize: '12px', minWidth: '100px' }} >{item.wbsDesc}</StyledTableCell>
                                        <StyledTableCell  >{item.costElement}</StyledTableCell>
                                        <StyledTableCell  >{item.glDesc}</StyledTableCell>
                                        <StyledTableCell  >{item.class6}</StyledTableCell>
                                        <StyledTableCell  >{item.class7}</StyledTableCell>
                                        <StyledTableCell  >{item.class8}</StyledTableCell>
                                        <StyledTableCell  >{item.all}</StyledTableCell>
                                        <StyledTableCell  >{item.cutBack}</StyledTableCell>
                                        <StyledTableCell  >{item.wipAsset}</StyledTableCell>
                                        <StyledTableCell  >{item.ctb}</StyledTableCell>
                                        <StyledTableCell >

                                            <CustomTextField fullWidth size="small" select variant="outlined" id="pptClassificationId" value={item.pptClassificationId} onChange={(e) => updateFieldValue(item.id, 'pptClassificationId', e.target.value)}
                                            >
                                                {pptClassification.map((ppt, index1) => {
                                                    return <MenuItem key={index1} value={ppt.id}>{ppt.name}</MenuItem >
                                                })}
                                            </CustomTextField>
                                        </StyledTableCell>
                                        <StyledTableCell >

                                            <CustomTextField fullWidth size="small" select variant="outlined" id="afsClassificationId" value={item.afsClassificationId} onChange={(e) => updateFieldValue(item.id, 'afsClassificationId', e.target.value)}
                                            >
                                                {afsClassification.map((afs, index) => {
                                                    return <MenuItem key={index} value={afs.id}>{afs.name}</MenuItem >
                                                })}
                                            </CustomTextField>
                                        </StyledTableCell>
                                        <StyledTableCell >

                                            <CustomTextField fullWidth size="small" select variant="outlined" id="reconciliationId" value={item.reconciliationId} onChange={(e) => updateFieldValue(item.id, 'reconciliationId', e.target.value)}
                                            >
                                                {reconciliation.map((rec, index) => {
                                                    return <MenuItem key={index} value={rec.id}>{rec.name}</MenuItem >
                                                })}
                                            </CustomTextField>
                                        </StyledTableCell>
                                        <StyledTableCell >

                                            <CustomTextField fullWidth size="small" select variant="outlined" id="pptRamsesDescId" value={item.pptRamsesDescId} onChange={(e) => updateFieldValue(item.id, 'pptRamsesDescId', e.target.value)}
                                            >
                                                {pptRamses.map((rames, index) => {
                                                    return <MenuItem key={index} value={rames.id}>{rames.name}</MenuItem >
                                                })}
                                            </CustomTextField>
                                        </StyledTableCell>
                                        <StyledTableCell >

                                            <CustomTextField fullWidth size="small" select variant="outlined" id="taxTermId" value={item.taxTermId} onChange={(e) => updateFieldValue(item.id, 'taxTermId', e.target.value)}
                                            >
                                                {taxTerm.map((taxterm, index) => {
                                                    return <MenuItem key={index} value={taxterm.id}>{taxterm.name}</MenuItem >
                                                })}
                                            </CustomTextField>
                                        </StyledTableCell>
                                        <StyledTableCell  >{formatShortDateString(item.createdAt, false)}</StyledTableCell>
                                        <StyledTableCell  >{formatShortDateString(item.updatedAt, false)}</StyledTableCell>

                                    </StyledTableRow>
                                })}

                                {filteredRecords?.length === 0 &&
                                    <tr>
                                        <StyledTableCell colSpan={13}><p style={{ fontSize: '11px', textAlign: 'center' }}>No record available...</p></StyledTableCell>
                                    </tr>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                <br />

                <nav>
                    <div className=" flex flex-row ">
                        <div className=" flex items-start ">
                            <div className="px-6 py-2">Rows per page</div>&nbsp;
                            < Grid item xs={12} lg={3} >
                                <CustomTextField fullWidth id="fullWidth" select variant="outlined" size="small" name="RequestsNumbers" value={recordsPerPage} onChange={(e) => setRecordsPerPage(Number(e.target.value))} >
                                    <MenuItem value="20">20</MenuItem >
                                    <MenuItem value="40">40</MenuItem >
                                    <MenuItem value="55">55</MenuItem >
                                    <MenuItem value="85">85</MenuItem >
                                    <MenuItem value="125">125</MenuItem >
                                    <MenuItem value="200">200</MenuItem >
                                    <MenuItem value="255">255</MenuItem >
                                    <MenuItem value="350">350</MenuItem >
                                    <MenuItem value="500">500</MenuItem >


                                </CustomTextField>
                            </Grid>
                        </div>
                        <div style={{ flexGrow: 1 }} />
                        <Stack spacing={2}>
                            <Pagination color="secondary" count={numOfPages} siblingCount={1} boundaryCount={1}
                                onChange={(event, value) => setCurrentPage(value)} page={currentPage} size="small" classes={{ ul: "PaginationUl" }} />
                        </Stack>

                    </div>
                    <br />


                </nav>
            </>
            <Dialog
                open={importDialogOpen}
                onClose={() => {
                    setImportDialogOpen(false);
                    setSelectedFile(null);
                }}
                fullWidth
                maxWidth="sm"
            >
                <div className="flex justify-between ">
                    <DialogTitle>Import CTB Records</DialogTitle>

                    <DialogActions style={{ width: '50%' }}>
                        <Button color="secondary" style={{ marginRight: 20 }} onClick={() => setImportDialogOpen(false)}>Close</Button>

                    </DialogActions>
                </div>

                <DialogContent dividers>
                    {!selectedFile ? (
                        <>
                            <div className="float-right" >
                                <Button variant="outlined" color="secondary" onClick={() => setChangeYear(!changeYear)}>Import For a Different Year </Button>
                            </div>
                            <br />
                            <br />
                            {changeYear &&
                                <div className='flex mt-2 mb-2'>
                                    <CustomTextField size="small" id="outlined-basic" placeholder="Filter By Year..." value={changedYear} onChange={(e) => setChangedYear(Number(e.target.value))} variant="outlined" sx={{ width: '70%' }} />&nbsp;&nbsp;
                                    <Button color='info' variant="contained" onClick={() => setYear(changedYear)} style={{ textTransform: 'none', fontSize: 12 }}> Change Year</Button>

                                </div>
                            }

                            <p style={{ fontSize: 13, marginBottom: 6 }}>
                                Please select an Excel file to import CTB records  for the year <b>{year}</b>.
                            </p>

                            {/* ✅ Expected file name format */}
                            <p
                                style={{
                                    fontSize: 12,
                                    background: "#f5f5f5",
                                    border: "1px dashed #ccc",
                                    padding: "6px 10px",
                                    borderRadius: 6,
                                    marginBottom: 12,
                                    color: "#555",
                                }}
                            >
                                <b>Expected file name:</b> <br />
                                <code>CTB_PPT_{year}_Unmapped.xlsx</code>
                            </p>

                            <Button
                                variant="contained"
                                component="label"
                                color="success"
                                fullWidth
                                sx={{ mt: 1 }}
                            >
                                Choose Excel File (.xlsx)
                                <input
                                    hidden
                                    type="file"
                                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files.length > 0) {
                                            const file = e.target.files[0];
                                            const fileName = file.name;

                                            // ✅ Validate filename pattern
                                            const match = fileName.match(/CTB_PPT_(\d{4})_Unmapped\.xlsx$/i);
                                            if (!match) {
                                                showAlertMessage("⚠️ Invalid file name format. Expected: CTB_PPT_YYYY_Unmapped.xlsx");
                                                e.target.value = "";
                                                return;
                                            }

                                            // ✅ Check year consistency
                                            const fileYear = Number(match[1]);
                                            if (fileYear !== year) {
                                                showAlertMessage(`⚠️ The file year (${fileYear}) does not match the selected year (${year}).`);
                                                e.target.value = "";
                                                return;
                                            }

                                            setSelectedFile(file);
                                        }
                                    }}
                                />
                            </Button>
                        </>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                border: "1px solid #ddd",
                                borderRadius: 6,
                                padding: "8px 12px",
                                backgroundColor: "#fafafa",
                            }}
                        >
                            <span style={{ fontSize: 13 }}>{selectedFile.name}</span>
                            <Button
                                color="error"
                                size="small"
                                variant="outlined"
                                onClick={() => setSelectedFile(null)}
                            >
                                Remove
                            </Button>
                        </div>
                    )}
                </DialogContent>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        padding: "10px 20px",
                        gap: 8,
                    }}
                >
                    <Button
                        onClick={() => {
                            setImportDialogOpen(false);
                            setSelectedFile(null);
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="success"
                        disabled={!selectedFile || isUploading}
                        onClick={async () => {
                            if (!selectedFile) return;

                            const formData = new FormData();
                            formData.append("MappedFile", selectedFile);

                            try {
                                setIsUploading(true);
                                const res = await uploadCBTRecordsExcelFile(formData, year);
                                setJobID(res.data.id);
                                setJobStatus(res.data.status);
                                console.log(jobStatus)
                                showAlertMessage(res.message);
                                setImportDialogOpen(false);
                                setSelectedFile(null);
                            } catch (err) {
                                showAlertMessage("❌ Upload failed. Please try again.");
                            } finally {
                                setIsUploading(false);
                            }
                        }}
                    >
                        {isUploading ? "Uploading..." : "Send"}
                    </Button>
                </div>
            </Dialog>
        </>
    )
}
export default CTBalanceRecords;